import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  User,
  CreditCard,
  Check,
  X,
  Camera,
  Lock,
  Loader2,
  ChevronDown,
  ChevronRight,
  Layout,
  Moon,
  Sun,
  Pencil,
  Users,
  History,
  Layers,
  Gift,
  Settings,
  LogOut,
  Bell,
  BadgeCheck,
  IdCard,
  Wallet,
  PackagePlus,
  Eye,
  Save,
  HardDrive,
} from 'lucide-react';
import { ATM_FH as fh, ATM_PAGE_MAX_CLASS, ATM_ACTIVE_USER_AVATAR } from '../config/atmosphere.js';
import { API_BASE } from '../config/api';

/** Evita javascript: e esquemas estranhos em links vindos das configurações. */
function isPublicHttpUrl(s) {
  const raw = String(s || '').trim();
  if (!/^https?:\/\//i.test(raw)) return false;
  try {
    const u = new URL(raw);
    return u.protocol === 'http:' || u.protocol === 'https:';
  } catch {
    return false;
  }
}
import {
  buildFeatureSections,
  getPlanFeatureValue,
  getMergedElementTiers,
  hasLimitationInGroup,
  planAllowsMyApiMenuClient,
  PLAN_LABEL_KEYS,
} from '../config/planFeatures';
import LanguageSwitcher from '../components/LanguageSwitcher';
import LegalFooter from '../components/LegalFooter';
import { IndiquePanel, CollaboratorsPanel, RecentActivityPanel, PaymentGatewayPanel } from '../components/LayoutPanels';

/** Ícone de check verde */
const CheckIcon = () => <Check size={18} className="text-emerald-500" strokeWidth={2.5} />;
/** Ícone de X cinza */
const XIcon = () => <X size={18} className="text-slate-300 dark:text-slate-600" strokeWidth={2} />;

/** Converte valor de feature para exibição */
function FeatureCell({ value, t }) {
  if (value === true || value === '✓' || value === 'check') return <CheckIcon />;
  if (value === false || value === '✗' || value === 'x') return <XIcon />;
  if (value === 'unlimited' || value === 'ilimitado') return <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{t ? t('common:unlimited') : 'Ilimitado'}</span>;
  if (typeof value === 'number') return <span className="text-sm font-medium text-slate-700 dark:text-slate-300">{value}</span>;
  if (typeof value === 'string') return <span className="text-sm text-slate-600 dark:text-slate-400">{value}</span>;
  return <XIcon />;
}

/** Formata preço em R$ */
function formatPrice(n) {
  return parseFloat(n || 0).toFixed(2).replace('.', ',');
}

/** Linha colapsável: header clicável + conteúdo expandido + hover para expandir */
function CollapsibleSection({ label, icon: Icon, expanded, onToggle, children, plans, getSummaryCell, itemCount, t }) {
  const hoverRef = React.useRef(null);

  const handleMouseEnter = () => {
    if (expanded) return;
    hoverRef.current = setTimeout(() => onToggle(), 400);
  };

  const handleMouseLeave = () => {
    if (hoverRef.current) {
      clearTimeout(hoverRef.current);
      hoverRef.current = null;
    }
  };

  React.useEffect(() => () => { if (hoverRef.current) clearTimeout(hoverRef.current); }, []);

  return (
    <>
      <tr
        onClick={onToggle}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-[#d4e0f5] dark:hover:bg-slate-700/60 cursor-pointer select-none transition-colors"
        title={expanded ? (t ? t('profile:clickToCollapse') : 'Clique para recolher') : (t ? t('profile:clickToExpand', { count: itemCount }) : `Clique ou passe o mouse para expandir (${itemCount} itens)`)}
      >
        <td className="p-4">
          <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
            {expanded ? <ChevronDown size={18} className="text-slate-400 flex-shrink-0" /> : <ChevronRight size={18} className="text-slate-400 flex-shrink-0" />}
            {Icon && <Icon size={18} className="text-slate-400 dark:text-slate-500 flex-shrink-0" />}
            <span className="font-medium">{label}</span>
            {!expanded && itemCount > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium">
                {t ? t('profile:items', { count: itemCount }) : `${itemCount} itens`}
              </span>
            )}
          </div>
        </td>
        {plans.map((p) => (
          <td key={p.id} className="p-4 text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-center items-center gap-1">
              {getSummaryCell ? getSummaryCell(p) : null}
            </div>
          </td>
        ))}
      </tr>
      {expanded && children}
    </>
  );
}

/** Abas de moeda: Real | Dólar | Euro */
const CURRENCY_TABS = [
  { id: 'brl', label: 'Real (R$)', labelKey: 'profile:currencyBRL' },
  { id: 'usd', label: 'Dólar (US$)', labelKey: 'profile:currencyUSD' },
  { id: 'eur', label: 'Euro (€)', labelKey: 'profile:currencyEUR' },
];

/** Modal de informações de pagamento (nome, email, CPF/CNPJ, razão social) */
function PaymentInfoModal({ open, onClose, user, plan, opts, onConfirm, t, couponMoodlePrefix = '' }) {
  // Add basic auto-fill from user object.
  const [cpfCnpj, setCpfCnpj] = useState(() => (user?.cpfCnpj || ''));
  const [razaoSocial, setRazaoSocial] = useState(() => (user?.razaoSocial || ''));
  const [phone, setPhone] = useState(() => (user?.phone || user?.mobilePhone || ''));
  const [postalCode, setPostalCode] = useState(() => (user?.postalCode || ''));
  const [address, setAddress] = useState(() => (user?.address || ''));
  const [addressNumber, setAddressNumber] = useState(() => (user?.addressNumber || ''));
  const [province, setProvince] = useState(() => (user?.province || ''));
  const [city, setCity] = useState(() => (user?.city || ''));
  const [state, setState] = useState(() => (user?.state || ''));
  const [couponCode, setCouponCode] = useState('');
  const [moodleStudentName, setMoodleStudentName] = useState('');

  const normPrefix = (couponMoodlePrefix || '').trim().toUpperCase();
  const couponTrimUpper = (couponCode || '').trim().toUpperCase();
  const showMoodleStudentField = !!(plan && normPrefix && couponTrimUpper.startsWith(normPrefix));

  useEffect(() => {
    if (open) {
      setCouponCode('');
      setMoodleStudentName('');
    }
  }, [open]);

  useEffect(() => {
    const cc = (couponCode || '').trim().toUpperCase();
    const pref = (couponMoodlePrefix || '').trim().toUpperCase();
    if (!pref || !cc.startsWith(pref)) setMoodleStudentName('');
  }, [couponCode, couponMoodlePrefix]);

  const handleSubmit = () => {
    const digits = (cpfCnpj || '').replace(/\D/g, '');
    if (opts?.currency === 'brl' && digits.length !== 11 && digits.length !== 14) {
      alert(t ? t('profile:paymentInfoCpfCnpjInvalid') : 'Informe CPF (11 dígitos) ou CNPJ (14 dígitos) para assinaturas em Real.');
      return;
    }
    const phoneDigits = (phone || '').replace(/\D/g, '');
    if (opts?.currency === 'brl' && phoneDigits.length < 10) {
      alert(t ? t('profile:paymentInfoPhoneInvalid') : 'Informe um telefone válido com DDD.');
      return;
    }
    if (opts?.currency === 'brl') {
      if (!postalCode) { alert(t ? t('profile:paymentInfoAddressCepInvalid') : 'O campo CEP é obrigatório.'); return; }
      if (!address) { alert(t ? t('profile:paymentInfoAddressStreetInvalid') : 'O campo Logradouro é obrigatório.'); return; }
      if (!addressNumber) { alert(t ? t('profile:paymentInfoAddressNumberInvalid') : 'O campo Número é obrigatório.'); return; }
      if (!province) { alert(t ? t('profile:paymentInfoAddressProvinceInvalid') : 'O campo Bairro é obrigatório.'); return; }
      if (!city) { alert(t ? t('profile:paymentInfoAddressCityInvalid') : 'O campo Cidade é obrigatório.'); return; }
      if (!state) { alert(t ? t('profile:paymentInfoAddressStateInvalid') : 'O campo UF (Estado) é obrigatório.'); return; }
    }
    const cc = (couponCode || '').trim().toUpperCase();
    const pref = (couponMoodlePrefix || '').trim().toUpperCase();
    if (plan && pref && cc.startsWith(pref)) {
      const mn = (moodleStudentName || '').trim();
      if (mn.length < 2) {
        alert(t ? t('profile:couponMoodleStudentRequired') : 'Informe o nome do aluno no Moodle (obrigatório para cupons com este prefixo).');
        return;
      }
    }
    // Passar sempre string de dígitos (para BRL já validado)
    onConfirm({
      cpfCnpj: digits,
      razaoSocial: (razaoSocial || '').trim(),
      phone: phoneDigits,
      postalCode: (postalCode || '').replace(/\D/g, ''),
      address: (address || '').trim(),
      addressNumber: (addressNumber || '').trim(),
      province: (province || '').trim(),
      city: (city || '').trim(),
      state: (state || '').trim().toUpperCase(),
      couponCode: plan ? (couponCode || '').trim().toUpperCase() : '',
      moodleStudentName: plan && showMoodleStudentField ? (moodleStudentName || '').trim() : '',
    });
  };

  const handleClose = () => {
    setCpfCnpj('');
    setRazaoSocial('');
    setPhone('');
    setPostalCode('');
    setAddress('');
    setAddressNumber('');
    setProvince('');
    setCity('');
    setState('');
    setCouponCode('');
    setMoodleStudentName('');
    onClose();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto overscroll-contain bg-black/50 p-4 sm:p-6">
      <div className="flex min-h-[min(100%,100dvh)] items-start justify-center py-6 sm:items-center sm:py-4">
        <div
          className="flex min-h-0 w-full max-w-md max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-600 dark:bg-slate-800"
          onClick={(e) => e.stopPropagation()}
        >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 p-4 dark:border-slate-600">
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
            {t ? t('profile:paymentInfoTitle') : 'Informações de pagamento'}
          </h3>
          <button type="button" onClick={handleClose} className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700">
            <X size={20} />
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoName') : 'Nome'}</label>
            <input type="text" value={user?.name || ''} readOnly className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoEmail') : 'E-mail'}</label>
            <input type="email" value={user?.email || ''} readOnly className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoCpfCnpj') : 'CPF ou CNPJ'}</label>
            <input
              type="text"
              value={cpfCnpj}
              onChange={(e) => setCpfCnpj(e.target.value)}
              placeholder={t ? t('profile:paymentInfoCpfCnpjPlaceholder') : '000.000.000-00 ou 00.000.000/0001-00'}
              className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={18}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoPhone') : 'Telefone com DDD'}</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={t ? t('profile:paymentInfoPhonePlaceholder') : '(00) 00000-0000'}
              className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              maxLength={15}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoRazaoSocial') : 'Razão social (se for CNPJ)'}</label>
            <input
              type="text"
              value={razaoSocial}
              onChange={(e) => setRazaoSocial(e.target.value)}
              placeholder={t ? t('profile:paymentInfoRazaoSocialPlaceholder') : 'Nome da empresa'}
              className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="pt-4 mt-2 border-t border-slate-200 dark:border-slate-600">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200 mb-4">{t ? t('profile:paymentInfoAddressTitle') : 'Endereço de Cobrança (Obrigatório)'}</h4>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoAddressCep') : 'CEP'}</label>
                <input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="00000-000" className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500" maxLength={9} />
              </div>
              <div></div>
            </div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoAddressStreet') : 'Logradouro (Rua/Av)'}</label>
                <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder={t ? t('profile:paymentInfoAddressStreetPlaceholder') : 'Rua Exemplo'} className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoAddressNumber') : 'Número'}</label>
                <input type="text" value={addressNumber} onChange={(e) => setAddressNumber(e.target.value)} placeholder="123" className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoAddressProvince') : 'Bairro'}</label>
                <input type="text" value={province} onChange={(e) => setProvince(e.target.value)} placeholder={t ? t('profile:paymentInfoAddressProvincePlaceholder') : 'Centro'} className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoAddressCity') : 'Cidade'}</label>
                <input type="text" value={city} onChange={(e) => setCity(e.target.value)} placeholder={t ? t('profile:paymentInfoAddressCityPlaceholder') : 'São Paulo'} className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{t ? t('profile:paymentInfoAddressState') : 'UF (Estado)'}</label>
                <input type="text" value={state} onChange={(e) => setState(e.target.value.toUpperCase())} placeholder="SP" className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500" maxLength={2} />
              </div>
            </div>
            {plan ? (
              <div className="pt-2">
                <label htmlFor="payment-modal-coupon" className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {t ? t('profile:couponLabel') : 'Cupom'}
                </label>
                <input
                  id="payment-modal-coupon"
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  autoComplete="off"
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500 uppercase tracking-wide"
                />
                {showMoodleStudentField ? (
                  <div className="mt-3">
                    <label htmlFor="payment-modal-moodle-student" className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                      {t ? t('profile:couponMoodleStudentLabel') : 'Nome do aluno no Moodle'}
                      <span className="text-red-500"> *</span>
                    </label>
                    <input
                      id="payment-modal-moodle-student"
                      type="text"
                      value={moodleStudentName}
                      onChange={(e) => setMoodleStudentName(e.target.value)}
                      autoComplete="name"
                      className="w-full px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-blue-500"
                      placeholder={t ? t('profile:couponMoodleStudentPlaceholder') : 'Como consta no Moodle'}
                    />
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {t ? t('profile:couponMoodleStudentHelp') : 'Obrigatório para cupons cujo código começa com o prefixo definido pelo administrador.'}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="flex flex-shrink-0 gap-3 border-t border-slate-200 bg-white p-4 dark:border-slate-600 dark:bg-slate-800">
          <button type="button" onClick={handleClose} className="flex-1 py-2.5 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-medium hover:bg-slate-50 dark:hover:bg-slate-700/50">
            {t ? t('profile:paymentInfoCancel') : 'Cancelar'}
          </button>
          <button type="button" onClick={handleSubmit} className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold">
            {t ? t('profile:paymentInfoContinue') : 'Continuar'}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

/** Tabela de planos com abas por moeda e seções colapsáveis */
function PlansTable({ plans, currentPlanId, onSelectPlan, t, user, requiresPlanSelection }) {
  const { i18n } = useTranslation('profile');
  const [currencyTab, setCurrencyTab] = useState('brl');
  const [billingAnnual, setBillingAnnual] = useState(true);
  const [checkoutPlanId, setCheckoutPlanId] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentModalData, setPaymentModalData] = useState(null);
  const [expandedGrid, setExpandedGrid] = useState(false);
  const [expandedBasic, setExpandedBasic] = useState(false);
  const [expandedExpert, setExpandedExpert] = useState(false);
  const [expandedPremium, setExpandedPremium] = useState(false);
  const [elementTierMap, setElementTierMap] = useState(() => getMergedElementTiers({}));
  const [planUiLabels, setPlanUiLabels] = useState({
    annual_pt: '',
    annual_en: '',
    save_badge_pt: '',
    save_badge_en: '',
    corporate_plan_url: '',
    coupon_moodle_student_prefix: '',
  });

  useEffect(() => {
    fetch(`${API_BASE}/feature-flags`)
      .then((r) => r.json())
      .then((d) => setElementTierMap(getMergedElementTiers(d.flags || {})))
      .catch(() => setElementTierMap(getMergedElementTiers({})));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/public/plan-ui-labels`)
      .then((r) => r.json())
      .then((d) =>
        setPlanUiLabels({
          annual_pt: d.annual_pt || '',
          annual_en: d.annual_en || '',
          save_badge_pt: d.save_badge_pt || '',
          save_badge_en: d.save_badge_en || '',
          corporate_plan_url: d.corporate_plan_url || '',
          coupon_moodle_student_prefix: d.coupon_moodle_student_prefix || '',
        })
      )
      .catch(() => {});
  }, []);

  const planUiLang = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'pt';
  const annualPaymentLabel =
    (planUiLang === 'en' ? planUiLabels.annual_en : planUiLabels.annual_pt).trim() ||
    (t ? t('profile:annualPayment') : '');
  const savePercentBadgeLabel =
    (planUiLang === 'en' ? planUiLabels.save_badge_en : planUiLabels.save_badge_pt).trim() ||
    (t ? t('profile:savePercentBadge') : '');

  const corporatePlanHref = isPublicHttpUrl(planUiLabels.corporate_plan_url) ? planUiLabels.corporate_plan_url.trim() : '';

  const getLabel = (feat) => (t && PLAN_LABEL_KEYS[feat.key] ? t(`plans:${PLAN_LABEL_KEYS[feat.key]}`) : feat.label);

  if (!plans?.length) {
    return (
      <div className="text-center py-16 text-slate-500 dark:text-slate-400">
        <CreditCard size={48} className="mx-auto mb-4 text-slate-300 dark:text-slate-600" />
        <p>{t ? t('profile:noPlans') : 'Nenhum plano disponível no momento.'}</p>
      </div>
    );
  }

  const sections = buildFeatureSections(plans, elementTierMap);
  const gridHasItems = sections.grid.items.length > 0;
  const getValue = (p, feat) => getPlanFeatureValue(p, feat, elementTierMap);
  const gridHasLimitation = hasLimitationInGroup(plans, sections.grid.items, getValue);
  const basicHasLimitation = hasLimitationInGroup(plans, sections.elementos.basic.items, getValue);
  const expertHasLimitation = hasLimitationInGroup(plans, sections.elementos.expert.items, getValue);
  const premiumHasLimitation = hasLimitationInGroup(plans, sections.elementos.premium.items, getValue);

  const hasPriceInCurrency = (currency) => {
    return plans.some((p) => {
      if (currency === 'brl') return parseFloat(p.price_brl || p.price_brl_monthly || 0) > 0 || parseFloat(p.price_brl_annual || 0) > 0;
      if (currency === 'usd') return parseFloat(p.price_usd || 0) > 0;
      if (currency === 'eur') return parseFloat(p.price_eur || 0) > 0;
      return false;
    });
  };

  const filtered = CURRENCY_TABS.filter((tab) => hasPriceInCurrency(tab.id));
  const visibleTabs = filtered.length > 0 ? filtered : [CURRENCY_TABS[0]];

  const getPlanPrice = (p, currency) => {
    if (currency === 'brl') {
      const annual = parseFloat(p.price_brl_annual || 0);
      const monthly = parseFloat(p.price_brl_monthly || p.price_brl || 0);
      if (monthly === 0 && annual === 0) return { display: '0,00', total: null, isAnnual: false, free: true, symbol: 'R$' };
      if (annual > 0 && billingAnnual) {
        const monthlyEquivalent = annual / 12;
        return { display: formatPrice(monthlyEquivalent), total: formatPrice(annual), isAnnual: true, free: false, symbol: 'R$' };
      }
      return { display: formatPrice(monthly), total: null, isAnnual: false, free: false, symbol: 'R$' };
    }
    if (currency === 'usd') {
      const monthly = parseFloat(p.price_usd || 0);
      if (monthly === 0) return { display: '0,00', total: null, isAnnual: false, free: true, symbol: 'US$' };
      return { display: formatPrice(monthly), total: null, isAnnual: false, free: false, symbol: 'US$' };
    }
    if (currency === 'eur') {
      const monthly = parseFloat(p.price_eur || 0);
      if (monthly === 0) return { display: '0,00', total: null, isAnnual: false, free: true, symbol: '€' };
      return { display: formatPrice(monthly), total: null, isAnnual: false, free: false, symbol: '€' };
    }
    return { display: '0,00', total: null, isAnnual: false, free: true, symbol: 'R$' };
  };

  const popularPlanId = useMemo(() => {
    if (!plans?.length) return null;
    const expert = plans.find((p) => /expert/i.test(String(p.name || '')));
    if (expert) return expert.id;
    return plans[Math.min(2, plans.length - 1)]?.id ?? null;
  }, [plans]);

  /** Índice do plano atual na lista (API já ordena por sort_order). Só bloqueia downgrade quando o usuário já tem plano. */
  const currentTierIndex = useMemo(() => {
    if (!currentPlanId || !plans?.length) return -1;
    const i = plans.findIndex((p) => p.id === currentPlanId);
    return i;
  }, [plans, currentPlanId]);

  const lockUpgradeTiers = !requiresPlanSelection && currentTierIndex >= 0;

  const openCheckout = async (p) => {
    const idx = plans.findIndex((x) => x.id === p.id);
    if (lockUpgradeTiers && idx >= 0 && currentTierIndex >= 0 && idx <= currentTierIndex) return;
    if (currentPlanId === p.id || checkoutPlanId) return;
    const opts = { currency: currencyTab, billingAnnual: currencyTab === 'brl' ? billingAnnual : false };
    const price = getPlanPrice(p, currencyTab);
    const hasPrice = !price.free;
    if (hasPrice) {
      setPaymentModalData({ p, opts });
      setPaymentModalOpen(true);
    } else {
      setCheckoutPlanId(p.id);
      try {
        await onSelectPlan?.(p, opts);
      } finally {
        setCheckoutPlanId(null);
      }
    }
  };

  const SummaryCheck = ({ hasLimitation, plan }) => {
    const hasAny = sections.grid.items.some((it) => getValue(plan, it));
    if (!hasAny) return <XIcon />;
    const limited = hasLimitation(plan);
    return (
      <span className="flex items-center justify-center gap-0.5">
        <CheckIcon />
        {limited && <span className="text-amber-500 font-bold" title={t('profile:planWithLimitation')}>*</span>}
      </span>
    );
  };

  const ElementSummaryCheck = ({ items, hasLimitation, plan }) => {
    const hasAny = items.some((it) => getValue(plan, it));
    if (!hasAny && items.length > 0) return <XIcon />;
    if (items.length === 0) return null;
    const limited = hasLimitation(plan);
    return (
      <span className="flex items-center justify-center gap-0.5">
        <CheckIcon />
        {limited && <span className="text-amber-500 font-bold" title={t('profile:planWithLimitation')}>*</span>}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="mb-10 text-center">
        <h2 className={`${fh} mb-3 text-3xl font-extrabold tracking-tight text-[#2c2f30] dark:text-slate-100 md:text-4xl`}>
          {t ? t('profile:plansHeroTitle') : ''}
        </h2>
        <p className="mx-auto mb-8 max-w-2xl text-base font-medium text-[#595c5d] dark:text-slate-400 md:text-lg">
          {t ? t('profile:plansHeroSubtitle') : ''}
        </p>
        {currencyTab === 'brl' ? (
          <div className="flex flex-wrap items-center justify-center gap-4">
            <span className="text-sm font-semibold text-[#595c5d] dark:text-slate-400">{t ? t('profile:monthlyPayment') : ''}</span>
            <button
              type="button"
              onClick={() => setBillingAnnual(!billingAnnual)}
              className={`relative h-8 w-14 rounded-full transition-colors ${billingAnnual ? 'bg-[#0546ed]' : 'bg-[#d1d5db]'}`}
              aria-pressed={billingAnnual}
            >
              <span
                className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-all ${billingAnnual ? 'left-7' : 'left-1'}`}
              />
            </button>
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold text-[#2c2f30] dark:text-slate-100">{annualPaymentLabel}</span>
              <span className="rounded-full bg-[#69f6b8] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#005a3c]">
                {savePercentBadgeLabel}
              </span>
            </div>
          </div>
        ) : null}
      </div>

      {/* Abas de moeda: Real | Dólar | Euro */}
      <div className="flex flex-wrap items-center justify-center gap-3 md:justify-start">
        <div className="flex gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-800">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setCurrencyTab(tab.id)}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${currencyTab === tab.id ? 'bg-white text-slate-800 shadow-sm dark:bg-slate-700 dark:text-slate-200' : 'text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}
            >
              {t ? t(tab.labelKey) : tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Cards de preço (Stitch planos.html) */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {plans.map((p) => {
          const price = getPlanPrice(p, currencyTab);
          const planIdx = plans.findIndex((x) => x.id === p.id);
          const isCurrent = currentPlanId === p.id;
          const tierBlocked = lockUpgradeTiers && planIdx >= 0 && currentTierIndex >= 0 && planIdx <= currentTierIndex;
          const isDowngradeLocked = lockUpgradeTiers && planIdx >= 0 && currentTierIndex >= 0 && planIdx < currentTierIndex;
          const isPopular = popularPlanId === p.id;
          const loading = checkoutPlanId === p.id;
          const free = price.free;
          
          let cardSurface = free
            ? 'bg-[#eff1f2] dark:bg-slate-800/90'
            : 'bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:bg-slate-800/90';
          
          if (isCurrent) {
            cardSurface = 'bg-white dark:bg-slate-800 border-2 border-blue-600 dark:border-blue-400 shadow-xl shadow-blue-600/10 dark:shadow-blue-400/5 ring-4 ring-blue-600/10 dark:ring-blue-400/5 z-10 scale-[1.01]';
          }

          const borderB = isPopular ? 'border-b-4 border-[#0546ed]' : 'border-b-4 border-transparent hover:border-[#0546ed] dark:hover:border-blue-500';
          return (
            <div
              key={p.id}
              className={`relative flex flex-col rounded-xl p-8 text-center transition-all duration-300 ${cardSurface} ${!free && !isCurrent ? borderB : 'border-b-4 border-transparent'}`}
            >
              {isPopular ? (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#0546ed] px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                  {t ? t('profile:planMostPopular') : 'Mais popular'}
                </span>
              ) : null}
              <h3 className={`${fh} mb-2 text-xl font-bold uppercase tracking-tight text-[#2c2f30] dark:text-slate-100`}>{p.name}</h3>
              <div className="mb-6 min-h-[3.5rem]">
                {free ? (
                  <span className="text-3xl font-black text-[#2c2f30] dark:text-slate-50">{t ? t('common:free') : 'Grátis'}</span>
                ) : (
                  <div className="flex flex-wrap items-baseline justify-center gap-1">
                    <span className="text-sm font-bold text-[#595c5d] dark:text-slate-400">{price.symbol}</span>
                    <span className="text-3xl font-black text-[#2c2f30] dark:text-slate-50">{price.display}</span>
                    <span className="text-sm font-medium text-[#595c5d] dark:text-slate-400">{t ? t('common:perMonth') : '/mês'}</span>
                  </div>
                )}
                {price.isAnnual && price.total ? (
                  <p className="mt-2 text-xs text-[#595c5d] dark:text-slate-500">
                    {t ? t('profile:annualTotal', { total: price.total }) : ''}
                  </p>
                ) : null}
              </div>
              <p className="mb-8 min-h-[40px] text-xs leading-relaxed text-[#595c5d] dark:text-slate-400">
                {p.description || '\u00a0'}
              </p>
              <button
                type="button"
                onClick={() => openCheckout(p)}
                disabled={loading || tierBlocked}
                title={isDowngradeLocked ? (t ? t('profile:planNoDowngradeHint') : '') : undefined}
                className={`${fh} mt-auto w-full rounded-xl py-3 text-sm font-bold transition-colors disabled:cursor-not-allowed ${
                  isCurrent
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 border border-blue-600 dark:border-blue-400 cursor-default shadow-sm'
                    : tierBlocked
                      ? 'cursor-not-allowed bg-[#dadddf] text-[#595c5d] dark:bg-slate-600 dark:text-slate-300'
                      : isPopular
                        ? 'bg-[#0546ed] text-white shadow-md shadow-[#0546ed]/20 hover:bg-[#003cd3]'
                        : 'bg-[#d6e3fb] text-[#465365] hover:bg-[#4f5c70] hover:text-white dark:bg-blue-950/60 dark:text-blue-100 dark:hover:bg-slate-600'
                }`}
              >
                {loading
                  ? (t ? t('profile:checkoutLoading') : 'Aguarde...')
                  : isCurrent
                    ? (t ? t('profile:currentPlan') : 'Plano atual')
                    : isDowngradeLocked
                      ? (t ? t('profile:planNoDowngrade') : 'Contratar')
                      : free
                        ? (t ? t('profile:start') : 'Começar')
                        : isPopular
                          ? (t ? t('profile:subscribeNow') : 'Assinar Agora')
                          : (t ? t('profile:subscribe') : 'Assinar')}
              </button>
            </div>
          );
        })}
      </div>

      {corporatePlanHref ? (
        <div className="flex flex-col items-center justify-center mt-10 mb-2 px-4">
          <a
            href={corporatePlanHref}
            target="_blank"
            rel="noopener noreferrer"
            className={`${fh} inline-flex flex-col items-center justify-center gap-0.5 sm:flex-row sm:gap-2 w-full max-w-lg px-6 py-4 rounded-2xl border-2 border-[#0546ed] bg-white dark:bg-slate-800/90 text-center text-[#0546ed] dark:text-blue-400 font-bold text-sm sm:text-base shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:bg-blue-50 dark:hover:bg-blue-950/50 transition-colors`}
          >
            <span>{t ? t('profile:corporatePlanButton') : 'Plano corporativo — Precisa de mais recursos?'}</span>
          </a>
          <span className="mt-2 text-xs text-[#595c5d] dark:text-slate-500">{t ? t('profile:corporatePlanButtonHint') : 'Abre em nova aba para contato comercial.'}</span>
        </div>
      ) : null}

      <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">
        {t ? t('profile:collapsibleHint') : 'Linhas com N itens são colapsáveis — clique ou passe o mouse para expandir.'}
      </p>

      <div className="w-full min-w-0 max-w-full overflow-x-auto rounded-2xl bg-white shadow-[0_8px_40px_rgb(0,0,0,0.03)] dark:bg-slate-800 dark:shadow-none">
        <table className="plan-table-zebra min-w-[640px] w-full text-left">
          <thead>
            <tr className="bg-[#e0e3e4] dark:bg-slate-700/90">
              <th className="px-8 py-6 text-sm font-bold uppercase tracking-wider text-[#2c2f30] dark:text-slate-200">
                {t ? t('profile:resource') : 'Recurso'}
              </th>
              {plans.map((p) => (
                <th key={p.id} className="min-w-[120px] px-8 py-6 text-center text-sm font-bold text-[#2c2f30] dark:text-slate-200">
                  {String(p.name || '').toUpperCase()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Base: cotas de armazenamento (MB), colaboradores, salvar layout */}
            {sections.base.map((feat) => (
              <tr key={feat.key} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-[#d4e0f5] dark:hover:bg-slate-700/60">
                <td className="p-4">
                  <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <feat.icon size={18} className="text-slate-400 dark:text-slate-500 flex-shrink-0" />
                    <span className="font-medium">{getLabel(feat)}</span>
                  </div>
                </td>
                {plans.map((p) => (
                  <td key={p.id} className="p-4 text-center">
                    <div className="flex justify-center">
                      <FeatureCell value={getValue(p, feat)} t={t} />
                    </div>
                  </td>
                ))}
              </tr>
            ))}

            {/* Grid - colapsável */}
            {gridHasItems && (
              <CollapsibleSection
                label={t ? t('plans:grids') : 'GRIDS (modelos de layout)'}
                icon={Layout}
                expanded={expandedGrid}
                onToggle={() => setExpandedGrid(!expandedGrid)}
                plans={plans}
                getSummaryCell={(p) => <SummaryCheck hasLimitation={gridHasLimitation} plan={p} />}
                itemCount={sections.grid.items.length}
                t={t}
              >
                {sections.grid.items.map((feat) => (
                  <tr key={feat.key} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-[#d4e0f5] dark:hover:bg-slate-700/60">
                    <td className="p-4 pl-10">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <span className="font-medium">{getLabel(feat)}</span>
                      </div>
                    </td>
                    {plans.map((p) => (
                      <td key={p.id} className="p-4 text-center">
                        <div className="flex justify-center">
                          <FeatureCell value={getValue(p, feat)} t={t} />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </CollapsibleSection>
            )}

            {/* Elementos Basic */}
            {sections.elementos.basic.items.length > 0 && (
              <CollapsibleSection
                label={t ? t('plans:elementsBasic') : 'Elementos Basic'}
                icon={Layout}
                expanded={expandedBasic}
                onToggle={() => setExpandedBasic(!expandedBasic)}
                plans={plans}
                getSummaryCell={(p) => <ElementSummaryCheck items={sections.elementos.basic.items} hasLimitation={basicHasLimitation} plan={p} />}
                itemCount={sections.elementos.basic.items.length}
                t={t}
              >
                {sections.elementos.basic.items.map((feat) => (
                  <tr key={feat.key} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-[#d4e0f5] dark:hover:bg-slate-700/60">
                    <td className="p-4 pl-10">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <span className="font-medium">{getLabel(feat)}</span>
                      </div>
                    </td>
                    {plans.map((p) => (
                      <td key={p.id} className="p-4 text-center">
                        <div className="flex justify-center">
                          <FeatureCell value={getValue(p, feat)} t={t} />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </CollapsibleSection>
            )}

            {/* Elementos Expert */}
            {sections.elementos.expert.items.length > 0 && (
              <CollapsibleSection
                label={t ? t('plans:elementsExpert') : 'Elementos Expert'}
                icon={Layout}
                expanded={expandedExpert}
                onToggle={() => setExpandedExpert(!expandedExpert)}
                plans={plans}
                getSummaryCell={(p) => <ElementSummaryCheck items={sections.elementos.expert.items} hasLimitation={expertHasLimitation} plan={p} />}
                itemCount={sections.elementos.expert.items.length}
                t={t}
              >
                {sections.elementos.expert.items.map((feat) => (
                  <tr key={feat.key} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-[#d4e0f5] dark:hover:bg-slate-700/60">
                    <td className="p-4 pl-10">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <span className="font-medium">{getLabel(feat)}</span>
                      </div>
                    </td>
                    {plans.map((p) => (
                      <td key={p.id} className="p-4 text-center">
                        <div className="flex justify-center">
                          <FeatureCell value={getValue(p, feat)} t={t} />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </CollapsibleSection>
            )}

            {/* Elementos Premium */}
            {sections.elementos.premium.items.length > 0 && (
              <CollapsibleSection
                label={t ? t('plans:elementsPremium') : 'Elementos Premium'}
                icon={Layout}
                expanded={expandedPremium}
                onToggle={() => setExpandedPremium(!expandedPremium)}
                plans={plans}
                getSummaryCell={(p) => <ElementSummaryCheck items={sections.elementos.premium.items} hasLimitation={premiumHasLimitation} plan={p} />}
                itemCount={sections.elementos.premium.items.length}
                t={t}
              >
                {sections.elementos.premium.items.map((feat) => (
                  <tr key={feat.key} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-[#d4e0f5] dark:hover:bg-slate-700/60">
                    <td className="p-4 pl-10">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400">
                        <span className="font-medium">{getLabel(feat)}</span>
                      </div>
                    </td>
                    {plans.map((p) => (
                      <td key={p.id} className="p-4 text-center">
                        <div className="flex justify-center">
                          <FeatureCell value={getValue(p, feat)} t={t} />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </CollapsibleSection>
            )}

            {/* Módulos (sempre visíveis) */}
            {sections.modules.map((feat) => (
              <tr key={feat.key} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-[#d4e0f5] dark:hover:bg-slate-700/60">
                <td className="p-4">
                  <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                    <feat.icon size={18} className="text-slate-400 dark:text-slate-500 flex-shrink-0" />
                    <span className="font-medium">{getLabel(feat)}</span>
                  </div>
                </td>
                {plans.map((p) => (
                  <td key={p.id} className="p-4 text-center">
                    <div className="flex justify-center">
                      <FeatureCell value={getValue(p, feat)} t={t} />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-20 flex flex-col items-center">
        <p className="text-center text-sm text-[#595c5d] dark:text-slate-400">
          {t ? t('profile:plansTrustCta') : ''}{' '}
          {corporatePlanHref ? (
            <a
              href={corporatePlanHref}
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-[#0546ed] hover:underline dark:text-blue-400"
            >
              {t ? t('profile:plansTrustCtaLink') : ''}
            </a>
          ) : (
            <span className="font-bold text-[#595c5d] dark:text-slate-500">{t ? t('profile:plansTrustCtaLink') : ''}</span>
          )}
        </p>
      </div>

      {/* Rodapé: explicação do * */}
      <p className="text-sm text-slate-500 dark:text-slate-400">
        <span className="font-semibold text-amber-600 dark:text-amber-500">*</span> {t ? t('profile:limitationNote') : '= plano com limitação neste recurso (possui menos itens que o plano completo).'}
      </p>

      <PaymentInfoModal
        open={paymentModalOpen}
        onClose={() => { setPaymentModalOpen(false); setPaymentModalData(null); }}
        user={user}
        plan={paymentModalData?.p}
        opts={paymentModalData?.opts}
        couponMoodlePrefix={planUiLabels.coupon_moodle_student_prefix || ''}
        onConfirm={(paymentData) => {
          if (!paymentModalData || !paymentData) return;
          const { p, opts } = paymentModalData;
          const cpfVal = (paymentData.cpfCnpj != null && paymentData.cpfCnpj !== '') ? String(paymentData.cpfCnpj).replace(/\D/g, '') : '';
          const razVal = (paymentData.razaoSocial || '').trim();
          const phoneVal = (paymentData.phone || '').trim();
          const postalCodeVal = (paymentData.postalCode || '').trim();
          const addressVal = (paymentData.address || '').trim();
          const addressNumberVal = (paymentData.addressNumber || '').trim();
          const provinceVal = (paymentData.province || '').trim();
          const cityVal = (paymentData.city || '').trim();
          const stateVal = (paymentData.state || '').trim();
          const couponVal = (paymentData.couponCode || '').trim();
          const moodleStudent = (paymentData.moodleStudentName || '').trim();

          setPaymentModalOpen(false);
          setPaymentModalData(null);
          setCheckoutPlanId(p.id);
          onSelectPlan?.(p, {
            ...opts,
            cpfCnpj: cpfVal,
            razaoSocial: razVal,
            phone: phoneVal,
            postalCode: postalCodeVal,
            address: addressVal,
            addressNumber: addressNumberVal,
            province: provinceVal,
            city: cityVal,
            state: stateVal,
            couponCode: couponVal,
            ...(moodleStudent ? { moodleStudentName: moodleStudent } : {}),
          }).finally(() => setCheckoutPlanId(null));
        }}
        t={t}
      />
    </div>
  );
}

function AddonGrantIcon({ grantType, className }) {
  const gt = String(grantType || '');
  const cn = className || 'text-slate-400 dark:text-slate-500';
  if (gt === 'editor') return <Pencil size={22} className={cn} strokeWidth={2} />;
  if (gt === 'viewer') return <Eye size={22} className={cn} strokeWidth={2} />;
  if (gt === 'save_slot') return <Save size={22} className={cn} strokeWidth={2} />;
  return <HardDrive size={22} className={cn} strokeWidth={2} />;
}

function addonPriceForCycle(addon, cycle) {
  if (cycle === 'annual') return parseFloat(addon.price_brl_annual) || 0;
  if (cycle === 'semiannual') return parseFloat(addon.price_brl_semiannual) || 0;
  return parseFloat(addon.price_brl_monthly) || 0;
}

function addonGrantSummaryLine(addon, t) {
  const q = Math.max(1, parseInt(addon.grant_quantity, 10) || 1);
  const type = String(addon.grant_type || '');
  const keyMap = {
    editor: 'profile:addonsGrantEditor',
    viewer: 'profile:addonsGrantViewer',
    save_slot: 'profile:addonsGrantSave',
    storage_projects: 'profile:addonsGrantStorageProjects',
    storage_logs: 'profile:addonsGrantStorageLogs',
    storage_media: 'profile:addonsGrantStorageMedia',
  };
  const labelKey = keyMap[type] || 'profile:addonsGrantUnknown';
  const label = t ? t(labelKey) : type;
  if (type.startsWith('storage_')) {
    return t ? t('profile:addonsMbUnit', { count: q }) : `+${q} MB — ${label}`;
  }
  return t ? t('profile:addonsQuantityUnit', { count: q }) + ` — ${label}` : `+${q} — ${label}`;
}

/** Loja de add-ons (BRL / Asaas); exige plano pago no backend */
function PlanAddonsShop({ authToken, user, t, onUserRefresh }) {
  const { i18n } = useTranslation('profile');
  const isPt = !i18n.language?.toLowerCase().startsWith('en');
  const [addons, setAddons] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [cycleByAddon, setCycleByAddon] = useState({});
  const [checkoutAddonId, setCheckoutAddonId] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [pendingAddon, setPendingAddon] = useState(null);

  const hasPaidPlan = useMemo(() => {
    if (!user?.plan) return false;
    if (String(user.plan.id) === 'plan_free') return false;
    const n = (x) => parseFloat(x || 0) || 0;
    return n(user.plan.price_brl_monthly) > 0 || n(user.plan.price_brl) > 0 || n(user.plan.price_brl_annual) > 0;
  }, [user]);

  useEffect(() => {
    fetch(`${API_BASE}/plan-addons`)
      .then((r) => r.json())
      .then((d) => {
        const list = d.addons || [];
        setAddons(list);
        const next = {};
        list.forEach((a) => {
          next[a.id] = 'monthly';
        });
        setCycleByAddon(next);
      })
      .catch(() => setAddons([]))
      .finally(() => setLoadingCatalog(false));
  }, []);

  const loadSubs = useCallback(() => {
    if (!authToken) {
      setSubs([]);
      return;
    }
    fetch(`${API_BASE}/tenant/addon-subscriptions`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => r.json())
      .then((d) => setSubs(d.subscriptions || []))
      .catch(() => setSubs([]));
  }, [authToken]);

  useEffect(() => {
    loadSubs();
  }, [loadSubs]);

  const setCycle = (addonId, cycle) => {
    setCycleByAddon((prev) => ({ ...prev, [addonId]: cycle }));
  };

  const cycleSuffix = (cycle) => {
    if (cycle === 'annual') return t ? t('profile:addonsPerYear') : '/ano';
    if (cycle === 'semiannual') return t ? t('profile:addonsPerSemester') : '/sem';
    return t ? t('profile:addonsPerMonth') : '/mês';
  };

  const subStatusLabel = (st) => {
    const s = String(st || '');
    if (s === 'active') return t ? t('profile:addonsStatusActive') : 'Ativo';
    if (s === 'pending_checkout') return t ? t('profile:addonsStatusPending') : 'Pendente';
    if (s === 'cancelled') return t ? t('profile:addonsStatusCancelled') : 'Cancelado';
    return t ? t('profile:addonsStatusPendingGeneric') : s;
  };

  const openCheckout = (addon) => {
    if (!hasPaidPlan) {
      alert(t ? t('profile:addonsPaidOnly') : 'Plano pago necessário.');
      return;
    }
    const cycle = cycleByAddon[addon.id] || 'monthly';
    if (addonPriceForCycle(addon, cycle) <= 0) {
      alert(t ? t('profile:addonsPriceUnavailable') : 'Preço indisponível.');
      return;
    }
    setPendingAddon({ addon, cycle });
    setPaymentModalOpen(true);
  };

  const confirmPayment = async (paymentData) => {
    if (!pendingAddon || !authToken) return;
    const { addon, cycle } = pendingAddon;
    setPaymentModalOpen(false);
    setCheckoutAddonId(addon.id);
    try {
      const r = await fetch(`${API_BASE}/tenant/addons/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          addonId: addon.id,
          billingCycle: cycle,
          cpfCnpj: paymentData.cpfCnpj,
          phone: paymentData.phone,
          razaoSocial: paymentData.razaoSocial,
          postalCode: paymentData.postalCode,
          address: paymentData.address,
          addressNumber: paymentData.addressNumber,
          province: paymentData.province,
          city: paymentData.city,
          state: paymentData.state,
        }),
      });
      let data = {};
      try {
        const text = await r.text();
        if (text) data = JSON.parse(text);
      } catch {
        data = {};
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      alert(data.error || (t ? t('profile:addonsCheckoutError') : 'Erro no checkout.'));
    } catch {
      alert(t ? t('profile:addonsCheckoutError') : 'Erro no checkout.');
    } finally {
      setCheckoutAddonId(null);
      setPendingAddon(null);
      loadSubs();
      onUserRefresh?.();
    }
  };

  return (
    <div className="space-y-10">
      <div className="text-center">
        <h2 className={`${fh} mb-3 text-3xl font-extrabold tracking-tight text-[#2c2f30] dark:text-slate-100 md:text-4xl`}>
          {t ? t('profile:addonsHeaderTitle') : 'Add-ons'}
        </h2>
        <p className="mx-auto max-w-2xl text-base font-medium text-[#595c5d] dark:text-slate-400 md:text-lg">
          {t ? t('profile:addonsIntro') : ''}
        </p>
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-500">{t ? t('profile:addonsBilledNote') : ''}</p>
      </div>

      {!hasPaidPlan ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-center text-sm font-semibold text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
          {t ? t('profile:addonsPaidOnly') : ''}
        </div>
      ) : null}

      {loadingCatalog ? (
        <div className="py-16 text-center text-[#595c5d] dark:text-slate-400">{t ? t('profile:addonsLoading') : '…'}</div>
      ) : !addons.length ? (
        <div className="py-12 text-center text-slate-500 dark:text-slate-400">{t ? t('profile:addonsNone') : ''}</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {addons.map((a) => {
            const cycle = cycleByAddon[a.id] || 'monthly';
            const price = addonPriceForCycle(a, cycle);
            const name = isPt ? a.name_pt : a.name_en;
            const desc = isPt ? a.description_pt : a.description_en;
            const loading = checkoutAddonId === a.id;
            return (
              <div
                key={a.id}
                className="relative flex flex-col rounded-xl border border-slate-200/80 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] dark:border-slate-700 dark:bg-slate-800/90"
              >
                <div className="mb-3 flex items-center gap-2">
                  <AddonGrantIcon grantType={a.grant_type} />
                  <span className={`${fh} text-xs font-bold uppercase tracking-wider text-[#0546ed] dark:text-blue-400`}>
                    {addonGrantSummaryLine(a, t)}
                  </span>
                </div>
                <h3 className={`${fh} mb-2 text-lg font-bold text-[#2c2f30] dark:text-slate-100`}>{name}</h3>
                <p className="mb-4 min-h-[48px] text-xs leading-relaxed text-[#595c5d] dark:text-slate-400">{desc || '\u00a0'}</p>
                <div className="mb-4 flex flex-wrap gap-1">
                  {(['monthly', 'semiannual', 'annual']).map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCycle(a.id, c)}
                      className={`rounded-lg px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide transition-colors ${
                        cycle === c
                          ? 'bg-[#0546ed] text-white dark:bg-blue-600'
                          : 'bg-[#eff1f2] text-slate-600 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-300'
                      }`}
                    >
                      {c === 'monthly' ? (t ? t('profile:addonsCycleMonthly') : 'Mensal') : null}
                      {c === 'semiannual' ? (t ? t('profile:addonsCycleSemiannual') : 'Semestral') : null}
                      {c === 'annual' ? (t ? t('profile:addonsCycleAnnual') : 'Anual') : null}
                    </button>
                  ))}
                </div>
                <div className="mb-4 min-h-[2.5rem]">
                  {price > 0 ? (
                    <div className="flex flex-wrap items-baseline justify-center gap-1">
                      <span className="text-sm font-bold text-[#595c5d] dark:text-slate-400">R$</span>
                      <span className="text-2xl font-black text-[#2c2f30] dark:text-slate-50">{formatPrice(price)}</span>
                      <span className="text-xs font-medium text-[#595c5d] dark:text-slate-400">{cycleSuffix(cycle)}</span>
                    </div>
                  ) : (
                    <span className="text-sm text-amber-600 dark:text-amber-400">{t ? t('profile:addonsPriceUnavailable') : '—'}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => openCheckout(a)}
                  disabled={loading || !hasPaidPlan || price <= 0}
                  className={`${fh} mt-auto w-full rounded-xl py-3 text-sm font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                    hasPaidPlan && price > 0
                      ? 'bg-[#0546ed] text-white shadow-md shadow-[#0546ed]/20 hover:bg-[#003cd3]'
                      : 'bg-[#d6e3fb] text-[#465365] dark:bg-blue-950/60 dark:text-blue-100'
                  }`}
                >
                  {loading ? (t ? t('profile:checkoutLoading') : '…') : t ? t('profile:addonsSubscribe') : 'Contratar'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {authToken ? (
        <div className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <h3 className={`${fh} mb-4 text-lg font-bold text-[#2c2f30] dark:text-slate-100`}>
            {t ? t('profile:addonsYourSubscriptions') : 'Assinaturas'}
          </h3>
          {!subs.length ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">—</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {subs.map((s) => (
                <li
                  key={s.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 bg-[#f5f6f7] px-3 py-2 dark:border-slate-600 dark:bg-slate-900/50"
                >
                  <span className="font-medium text-slate-800 dark:text-slate-200">{isPt ? s.name_pt : s.name_en}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {s.billing_cycle} · {subStatusLabel(s.status)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <PaymentInfoModal
        open={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false);
          setPendingAddon(null);
        }}
        user={user}
        plan={null}
        opts={{ currency: 'brl' }}
        onConfirm={confirmPayment}
        t={t}
      />
    </div>
  );
}

const inputBase =
  'w-full rounded-xl border border-transparent bg-[#eff1f2] px-4 py-3 text-[15px] font-medium text-[#2c2f30] transition-all placeholder:text-[#9b9d9e] focus:border-[#0546ed] focus:ring-2 focus:ring-[#0546ed]/20 dark:bg-slate-800/90 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-blue-500 dark:focus:ring-blue-500/25';

const labelBase =
  'ml-1 text-xs font-bold uppercase tracking-wide text-[#595c5d] dark:text-slate-400';

/** Formulário de edição de perfil (layout bento — Stitch) */
function ProfileEditForm({ user, authToken, onSuccess, t, onCancel }) {
  const [name, setName] = useState(user?.name || '');
  const [cpfCnpj, setCpfCnpj] = useState(user?.cpfCnpj || '');
  const [razaoSocial, setRazaoSocial] = useState(user?.razaoSocial || '');
  const [phone, setPhone] = useState(user?.phone || user?.mobilePhone || '');
  const [postalCode, setPostalCode] = useState(user?.postalCode || '');
  const [address, setAddress] = useState(user?.address || '');
  const [addressNumber, setAddressNumber] = useState(user?.addressNumber || '');
  const [province, setProvince] = useState(user?.province || '');
  const [city, setCity] = useState(user?.city || '');
  const [state, setState] = useState(user?.state || '');
  const [complement, setComplement] = useState('');
  const [pwdOpen, setPwdOpen] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    setName(user?.name || '');
    setCpfCnpj(user?.cpfCnpj || '');
    setRazaoSocial(user?.razaoSocial || '');
    setPhone(user?.phone || user?.mobilePhone || '');
    setPostalCode(user?.postalCode || '');
    setAddress(user?.address || '');
    setAddressNumber(user?.addressNumber || '');
    setProvince(user?.province || '');
    setCity(user?.city || '');
    setState(user?.state || '');
    setAvatarUrl(user?.avatar_url || '');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setComplement('');
    setPwdOpen(false);
    setMsg({ type: '', text: '' });
  }, [
    user?.id,
    user?.name,
    user?.email,
    user?.cpfCnpj,
    user?.razaoSocial,
    user?.phone,
    user?.mobilePhone,
    user?.postalCode,
    user?.address,
    user?.addressNumber,
    user?.province,
    user?.city,
    user?.state,
    user?.avatar_url,
  ]);

  const resetForm = () => {
    setName(user?.name || '');
    setCpfCnpj(user?.cpfCnpj || '');
    setRazaoSocial(user?.razaoSocial || '');
    setPhone(user?.phone || user?.mobilePhone || '');
    setPostalCode(user?.postalCode || '');
    setAddress(user?.address || '');
    setAddressNumber(user?.addressNumber || '');
    setProvince(user?.province || '');
    setCity(user?.city || '');
    setState(user?.state || '');
    setAvatarUrl(user?.avatar_url || '');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setComplement('');
    setPwdOpen(false);
    setMsg({ type: '', text: '' });
    onCancel?.();
  };

  const planHasStorageQuota = [user?.plan?.storage_projects_mb, user?.plan?.storage_logs_mb, user?.plan?.storage_media_mb].some(
    (v) => v != null && Number(v) > 0,
  );
  const storagePct = planHasStorageQuota
    ? typeof user?.storage_used_percent === 'number'
      ? Math.min(100, Math.max(0, user.storage_used_percent))
      : 0
    : null;

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !authToken) return;
    setUploading(true);
    setMsg({ type: '', text: '' });
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch(`${API_BASE}/auth/avatar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
        body: fd,
      });
      const d = await r.json();
      if (r.ok && d.avatar_url) {
        setAvatarUrl(d.avatar_url);
        setMsg({ type: 'success', text: t ? t('profile:photoUpdated') : 'Foto atualizada!' });
        onSuccess?.({ ...user, avatar_url: d.avatar_url });
      } else {
        setMsg({ type: 'error', text: d.error || (t ? t('common:error') : 'Erro ao enviar.') });
      }
    } catch {
      setMsg({ type: 'error', text: 'Erro de conexão.' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMsg({ type: '', text: '' });
    if (newPassword && newPassword !== confirmPassword) {
      setMsg({ type: 'error', text: t ? t('profile:passwordsMismatch') : 'As senhas não coincidem.' });
      return;
    }
    if (newPassword && newPassword.length < 6) {
      setMsg({ type: 'error', text: t ? t('profile:passwordMinLength') : 'Nova senha deve ter pelo menos 6 caracteres.' });
      return;
    }
    if (newPassword && !currentPassword) {
      setMsg({ type: 'error', text: t ? t('profile:currentPasswordRequired') : 'Informe a senha atual para alterar.' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        cpfCnpj: (cpfCnpj || '').replace(/\D/g, ''),
        razaoSocial: (razaoSocial || '').trim(),
        phone: (phone || '').replace(/\D/g, ''),
        postalCode: (postalCode || '').replace(/\D/g, ''),
        address: (address || '').trim(),
        addressNumber: (addressNumber || '').trim(),
        province: (province || '').trim(),
        city: (city || '').trim(),
        state: (state || '').trim().toUpperCase()
      };
      if (newPassword) {
        body.currentPassword = currentPassword;
        body.newPassword = newPassword;
      }
      const r = await fetch(`${API_BASE}/auth/profile`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (r.ok) {
        setMsg({ type: 'success', text: t ? t('profile:profileUpdated') : 'Perfil atualizado!' });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        onSuccess?.(d.user);
      } else {
        setMsg({ type: 'error', text: d.error || 'Erro ao salvar.' });
      }
    } catch {
      setMsg({ type: 'error', text: 'Erro de conexão.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-8 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-1">
        <div className="flex flex-col items-center rounded-xl bg-white p-8 text-center shadow-sm dark:border dark:border-slate-700 dark:bg-slate-800">
          <div className="group relative mb-6 cursor-pointer">
            <div className="relative">
              <div className="h-32 w-32 overflow-hidden rounded-2xl bg-[#eff1f2] ring-4 ring-[#eff1f2] transition-transform group-hover:scale-[1.02] dark:bg-slate-700 dark:ring-slate-700">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <User size={48} className="text-[#0546ed]/50 dark:text-blue-400/60" />
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                  {uploading ? <Loader2 size={28} className="animate-spin text-white" /> : <Camera size={28} className="text-white" />}
                </div>
              </div>
              <label
                htmlFor="profile-avatar-upload"
                className="absolute -bottom-2 -right-2 cursor-pointer rounded-lg bg-[#003cd3] p-2 text-white shadow-lg transition-transform hover:scale-110 dark:bg-blue-600"
                title={t ? t('profile:clickToChange') : ''}
              >
                <Pencil size={18} />
              </label>
            </div>
            <input id="profile-avatar-upload" type="file" accept="image/*" className="hidden" onChange={handleAvatarChange} disabled={uploading} />
          </div>
          <h3 className={`${fh} text-xl font-bold text-[#2c2f30] dark:text-slate-100`}>{name || user?.name || '—'}</h3>
          <p className="mb-6 text-sm font-medium text-[#595c5d] dark:text-slate-400">{user?.email || ''}</p>
          <p className="mb-1 text-[11px] font-medium text-[#595c5d] dark:text-slate-500">{t ? t('profile:photoHint') : ''}</p>
          <div className="w-full border-t border-[#e6e8ea] pt-6 dark:border-slate-600">
            {storagePct != null ? (
              <>
                <div className="mb-2 flex justify-between text-xs font-semibold uppercase text-[#595c5d] dark:text-slate-400">
                  <span>{t ? t('profile:usedSpace') : ''}</span>
                  <span>{Math.round(storagePct)}%</span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#e6e8ea] dark:bg-slate-600">
                  <div className="h-full rounded-full bg-[#003cd3] transition-all dark:bg-blue-500" style={{ width: `${storagePct}%` }} />
                </div>
              </>
            ) : (
              <p className="text-center text-xs font-semibold uppercase text-[#595c5d] dark:text-slate-400">
                {t ? t('profile:storageUnlimited') : ''}
              </p>
            )}
            {user?.storage_usage ? (
              <p className="mt-3 text-left text-[10px] leading-relaxed text-[#595c5d]/85 dark:text-slate-500">
                {t
                  ? t('profile:storageBreakdown', {
                      p: user.storage_usage.projects_mb,
                      l: user.storage_usage.logs_mb,
                      m: user.storage_usage.media_mb,
                      total: user.storage_usage.total_mb,
                    })
                  : ''}
              </p>
            ) : null}
          </div>
        </div>

        <div className="space-y-4 rounded-xl bg-[#eff1f2] p-6 dark:bg-slate-800/80">
          <h4 className={`${fh} flex items-center gap-2 text-sm font-bold text-[#2c2f30] dark:text-slate-100`}>
            <Lock size={18} className="text-[#0546ed] dark:text-blue-400" />
            {t ? t('profile:accountSecurity') : 'Segurança da conta'}
          </h4>
          <p className="text-xs leading-relaxed text-[#595c5d] dark:text-slate-400">{t ? t('profile:passwordLastChangedHint') : ''}</p>
          <button
            type="button"
            onClick={() => setPwdOpen(true)}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-white py-2.5 text-sm font-bold text-[#0546ed] transition-colors hover:bg-[#f5f6f7] dark:bg-slate-900 dark:text-blue-400 dark:hover:bg-slate-800"
          >
            {t ? t('profile:changePasswordButton') : 'Trocar senha'}
          </button>
          {pwdOpen ? (
            <div className="space-y-3 border-t border-[#e6e8ea] pt-4 dark:border-slate-600">
              <p className="text-xs text-[#595c5d] dark:text-slate-500">{t ? t('profile:securityHint') : ''}</p>
              <div className="space-y-2">
                <label className={labelBase}>{t ? t('profile:currentPassword') : ''}</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={inputBase}
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>
              <div className="space-y-2">
                <label className={labelBase}>{t ? t('profile:newPassword') : ''}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={inputBase}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
              <div className="space-y-2">
                <label className={labelBase}>{t ? t('profile:confirmPassword') : ''}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className={inputBase}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="space-y-6 lg:col-span-2">
        <div className="rounded-xl bg-white p-8 shadow-sm dark:border dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0546ed]/10 dark:bg-blue-500/15">
              <IdCard size={22} className="text-[#0546ed] dark:text-blue-400" />
            </div>
            <h3 className={`${fh} text-lg font-bold text-[#2c2f30] dark:text-slate-100`}>{t ? t('profile:generalInfo') : ''}</h3>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:fullName') : ''}</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={inputBase}
                placeholder={t ? t('profile:namePlaceholder') : ''}
              />
            </div>
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:accessEmail') : ''}</label>
              <input type="email" value={user?.email || ''} readOnly className={`${inputBase} cursor-not-allowed opacity-90`} />
              <p className="ml-1 text-[11px] text-[#757778] dark:text-slate-500">{t ? t('profile:emailReadOnly') : ''}</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-white p-8 shadow-sm dark:border dark:border-slate-700 dark:bg-slate-800">
          <div className="mb-8 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#006947]/10 dark:bg-emerald-500/15">
              <Wallet size={22} className="text-[#006947] dark:text-emerald-400" />
            </div>
            <h3 className={`${fh} text-lg font-bold text-[#2c2f30] dark:text-slate-100`}>{t ? t('profile:billingInfo') : ''}</h3>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:paymentInfoCpfCnpj') : ''}</label>
              <input
                type="text"
                value={cpfCnpj}
                onChange={(e) => setCpfCnpj(e.target.value)}
                placeholder={t ? t('profile:paymentInfoCpfCnpjPlaceholder') : ''}
                className={inputBase}
                maxLength={18}
              />
            </div>
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:paymentInfoPhone') : ''}</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t ? t('profile:paymentInfoPhonePlaceholder') : ''}
                className={inputBase}
                maxLength={15}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <label className={labelBase}>{t ? t('profile:paymentInfoRazaoSocial') : ''}</label>
              <input
                type="text"
                value={razaoSocial}
                onChange={(e) => setRazaoSocial(e.target.value)}
                placeholder={t ? t('profile:paymentInfoRazaoSocialPlaceholder') : ''}
                className={inputBase}
              />
            </div>
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:paymentInfoAddressCep') : ''}</label>
              <input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} placeholder="00000-000" className={inputBase} maxLength={9} />
            </div>
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:paymentInfoAddressStreet') : ''}</label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={t ? t('profile:paymentInfoAddressStreetPlaceholder') : ''}
                className={inputBase}
              />
            </div>
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:paymentInfoAddressNumber') : ''}</label>
              <input type="text" value={addressNumber} onChange={(e) => setAddressNumber(e.target.value)} placeholder="123" className={inputBase} />
            </div>
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:complementLabel') : ''}</label>
              <input
                type="text"
                value={complement}
                onChange={(e) => setComplement(e.target.value)}
                placeholder={t ? t('profile:complementPlaceholder') : ''}
                className={inputBase}
              />
            </div>
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:paymentInfoAddressProvince') : ''}</label>
              <input
                type="text"
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                placeholder={t ? t('profile:paymentInfoAddressProvincePlaceholder') : ''}
                className={inputBase}
              />
            </div>
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:paymentInfoAddressCity') : ''}</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={t ? t('profile:paymentInfoAddressCityPlaceholder') : ''}
                className={inputBase}
              />
            </div>
            <div className="space-y-2">
              <label className={labelBase}>{t ? t('profile:paymentInfoAddressState') : ''}</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase())}
                placeholder="SP"
                className={inputBase}
                maxLength={2}
              />
            </div>
          </div>
        </div>

        {msg.text ? (
          <div
            className={`rounded-xl p-4 text-sm ${
              msg.type === 'success'
                ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-900/25 dark:text-emerald-300'
                : 'bg-red-50 text-red-800 dark:bg-red-900/25 dark:text-red-300'
            }`}
          >
            {msg.text}
          </div>
        ) : null}

        <div className="flex flex-col items-center justify-end gap-4 pt-2 sm:flex-row">
          <button
            type="button"
            onClick={resetForm}
            className={`${fh} w-full rounded-xl px-8 py-4 text-sm font-bold text-[#595c5d] transition-colors hover:text-[#2c2f30] dark:text-slate-400 dark:hover:text-slate-200 sm:w-auto`}
          >
            {t ? t('profile:cancel') : 'Cancelar'}
          </button>
          <button
            type="submit"
            disabled={saving}
            className={`${fh} flex w-full items-center justify-center gap-2 rounded-xl bg-[linear-gradient(135deg,#0546ed_0%,#003cd3_100%)] px-12 py-4 text-sm font-extrabold uppercase tracking-widest text-white shadow-lg shadow-[#0546ed]/25 transition-all hover:scale-[1.02] active:scale-95 disabled:opacity-50 dark:shadow-blue-900/40 sm:w-auto`}
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : null}
            {t ? t('profile:saveChanges') : 'Salvar alterações'}
          </button>
        </div>
      </div>
    </form>
  );
}

export default function ProfilePage({ user, authToken, darkMode, onToggleDarkMode, onBack, onLogout, onUserUpdate, onOpenProjectById, requiresPlanSelection, onPlanSelected, entryTab: entryTabProp, initialPaymentGatewayProvider = null }) {
  const { t } = useTranslation(['profile', 'plans', 'common', 'collab']);
  const [tab, setTab] = useState(() => entryTabProp ?? 'planos');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlanId, setCurrentPlanId] = useState(null);
  const [localUser, setLocalUser] = useState(user);
  /** Dentro de Planos: tabela de planos ou loja de add-ons */
  const [plansSubTab, setPlansSubTab] = useState('catalog');
  const hasTenant = !!(user?.tenant_id);
  /** Conta criada só como colaboradora no tenant de outrem: sem loja de planos / checkout. */
  const isGuestCollabAccount = localUser?.tenant_access === 'collab';

  useEffect(() => {
    if (requiresPlanSelection) setPlansSubTab('catalog');
  }, [requiresPlanSelection]);

  useEffect(() => {
    setLocalUser(user);
    if (user?.plan?.id) {
      setCurrentPlanId(user.plan.id);
    }
  }, [user]);

  useEffect(() => {
    if (entryTabProp != null && entryTabProp !== '') {
      setTab(entryTabProp);
    }
  }, [entryTabProp]);

  useEffect(() => {
    if (isGuestCollabAccount && (tab === 'planos' || tab === 'gateways')) setTab('perfil');
  }, [isGuestCollabAccount, tab]);

  useEffect(() => {
    if (authToken) {
      if (isGuestCollabAccount) {
        setPlans([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      fetch(`${API_BASE}/plans`, { headers: { Authorization: `Bearer ${authToken}` } })
        .then((r) => r.json())
        .then((d) => setPlans(d.plans || []))
        .catch(() => setPlans([]))
        .finally(() => setLoading(false));
    }
  }, [authToken, isGuestCollabAccount]);

  const handleUserUpdate = (updated) => {
    setLocalUser((prev) => {
      const next = {
        ...prev,
        ...updated,
        plan: updated.plan ?? prev?.plan,
        storage_usage: updated.storage_usage ?? prev?.storage_usage,
        storage_used_percent: updated.storage_used_percent ?? prev?.storage_used_percent,
      };
      onUserUpdate?.(next);
      return next;
    });
  };

  const refreshUserFromApi = useCallback(async () => {
    if (!authToken) return;
    try {
      const r = await fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${authToken}` } });
      const d = await r.json();
      if (d.user) handleUserUpdate(d.user);
    } catch {
      /* ignore */
    }
  }, [authToken]);

  // Retorno do checkout Asaas (URL profile?checkout=success): fallback se App.jsx não sincronizou
  useEffect(() => {
    if (!authToken || !hasTenant) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') !== 'success') return;
    fetch(`${API_BASE}/checkout/confirm-return`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d?.ok) refreshUserFromApi();
        const u = new URL(window.location.href);
        u.searchParams.delete('checkout');
        u.searchParams.delete('addon');
        window.history.replaceState({}, '', u.pathname + u.search);
      })
      .catch(() => { });
  }, [authToken, hasTenant, refreshUserFromApi]);

  const handleSelectPlan = async (p, opts = {}) => {
    const currency = opts?.currency ?? 'brl';
    const billingAnnual = opts?.billingAnnual ?? true;
    const cpfCnpj = (opts?.cpfCnpj || '').toString().replace(/\D/g, '');
    const priceBrl = parseFloat(p.price_brl || p.price_brl_monthly || p.price_brl_annual || 0);
    const priceUsd = parseFloat(p.price_usd || 0);
    const priceEur = parseFloat(p.price_eur || 0);
    const hasPrice = (currency === 'brl' && (priceBrl > 0 || parseFloat(p.price_brl_annual || 0) > 0)) || (currency === 'usd' && priceUsd > 0) || (currency === 'eur' && priceEur > 0);

    if (currency === 'brl' && hasPrice && cpfCnpj.length !== 11 && cpfCnpj.length !== 14) {
      alert(t ? t('profile:cpfCnpjRequired') || 'Informe CPF (11 dígitos) ou CNPJ (14 dígitos) para assinaturas em Real.' : 'Informe CPF (11 dígitos) ou CNPJ (14 dígitos) para assinaturas em Real.');
      return;
    }

    const razaoSocial = (opts?.razaoSocial || '').toString().trim();
    const phone = (opts?.phone || '').toString().trim();
    const postalCode = (opts?.postalCode || '').toString().trim();
    const address = (opts?.address || '').toString().trim();
    const addressNumber = (opts?.addressNumber || '').toString().trim();
    const province = (opts?.province || '').toString().trim();
    const city = (opts?.city || '').toString().trim();
    const state = (opts?.state || '').toString().trim();
    const couponCodeTrim = (opts?.couponCode || '').toString().trim();
    const moodleStudentTrim = (opts?.moodleStudentName || '').toString().trim();

    if (!hasTenant) {
      try {
        const body = { planId: p.id, currency, billingCycle: billingAnnual ? 'annual' : 'monthly' };
        if (couponCodeTrim) body.couponCode = couponCodeTrim;
        if (moodleStudentTrim) body.moodleStudentName = moodleStudentTrim;
        if (currency === 'brl') {
          body.cpfCnpj = cpfCnpj;
          body.phone = phone;
          if (postalCode) {
            Object.assign(body, { postalCode, address, addressNumber, province, city, state });
          }
        }
        if (razaoSocial) body.razaoSocial = razaoSocial;
        const r = await fetch(`${API_BASE}/tenant/self-signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify(body),
        });
        const data = await r.json();
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl;
          return;
        }
        if (data.success && data.user && data.token) {
          onPlanSelected?.(data.user, data.token);
          return;
        }
        alert(data.error || 'Erro ao criar conta. Tente novamente.');
      } catch (e) {
        alert('Erro de conexão. Tente novamente.');
      }
      return;
    }

    if (hasPrice) {
      try {
        const body = { planId: p.id, currency, billingCycle: billingAnnual ? 'annual' : 'monthly' };
        if (couponCodeTrim) body.couponCode = couponCodeTrim;
        if (moodleStudentTrim) body.moodleStudentName = moodleStudentTrim;
        if (currency === 'brl') {
          body.cpfCnpj = cpfCnpj;
          body.phone = phone;
          if (postalCode) {
            Object.assign(body, { postalCode, address, addressNumber, province, city, state });
          }
        }
        if (razaoSocial) body.razaoSocial = razaoSocial;
        const r = await fetch(`${API_BASE}/checkout/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify(body),
        });
        let data = {};
        try {
          const text = await r.text();
          if (text) data = JSON.parse(text);
        } catch (_) {
          data = { error: r.ok ? 'Resposta inválida do servidor.' : `Erro ${r.status}. Tente novamente.` };
        }
        if (data.url) {
          window.location.href = data.url;
          return;
        }
        if (data.activatedWithoutGateway) {
          await refreshUserFromApi();
          alert(t ? t('profile:planActivatedWithCoupon') : 'Plano ativado com o cupom — não foi necessário abrir o pagamento.');
          return;
        }
        alert(data.error || `Erro ao criar checkout. (${r.status})`);
      } catch (e) {
        alert('Erro de conexão. Tente novamente.');
      }
    }
  };

  const navItemClass = (active) =>
    `flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold transition-all duration-200 ${
      active
        ? 'border-r-4 border-[#0546ed] bg-[#eff1f2] font-bold text-[#0546ed] dark:border-blue-500 dark:bg-slate-800 dark:text-blue-400'
        : 'text-slate-500 hover:bg-[#eff1f2] hover:text-[#0546ed] dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-blue-400'
    }`;

  const showIndique = localUser?.plan?.features?.modules?.indique !== false;
  const showPaymentGatewayNavItem = !isGuestCollabAccount;
  const paymentGatewayNavLocked =
    !!localUser &&
    localUser.role !== 'super_admin' &&
    !planAllowsMyApiMenuClient(localUser?.plan?.features);

  const headerTitle =
    tab === 'planos' && plansSubTab === 'addons'
      ? t('profile:addonsHeaderTitle')
      : tab === 'planos'
        ? t('profile:plansHeaderTitle')
      : tab === 'colaboradores'
        ? t('collab:title')
        : tab === 'gateways'
          ? t('profile:paymentGatewayTitle')
          : t('profile:brandPrecision');

  const sidebarAvatarWrap =
    tab === 'perfil'
      ? 'h-12 w-12 overflow-hidden rounded-xl border-2 border-white bg-[#eff1f2] shadow-sm dark:border-slate-700 dark:bg-slate-800'
      : tab === 'indique'
        ? 'h-10 w-10 overflow-hidden rounded-xl border-2 border-white bg-[#eff1f2] shadow-sm dark:border-slate-700 dark:bg-slate-800'
        : 'h-10 w-10 overflow-hidden rounded-full border-2 border-white bg-[#eff1f2] shadow-sm dark:border-slate-700 dark:bg-slate-800';

  const sidebarPlanLineClass =
    tab === 'colaboradores'
      ? `${fh} truncate text-[10px] font-semibold uppercase tracking-wider text-[#0546ed] dark:text-blue-400`
      : `${fh} truncate text-xs font-medium text-[#595c5d] dark:text-slate-400`;

  const showSidebarUpgrade = !isGuestCollabAccount && (tab === 'alteracoes' || tab === 'colaboradores');

  const headerAvatarSrc = localUser?.avatar_url || ATM_ACTIVE_USER_AVATAR;

  return (
    <div className="flex min-h-screen w-full max-w-full min-w-0 overflow-x-hidden bg-[#f5f6f7] text-[#2c2f30] dark:bg-slate-950 dark:text-slate-100">
      {!requiresPlanSelection && (
        <aside className="fixed left-0 top-0 z-50 hidden h-screen w-64 flex-col border-r border-slate-200 bg-[#f5f6f7] px-4 py-8 dark:border-slate-700 dark:bg-slate-900 lg:flex">
          <div className="mb-10 px-2">
            <span className={`${fh} text-2xl font-black tracking-tight text-[#0546ed] dark:text-blue-400`}>
              {t('profile:appBrandShort', { defaultValue: 'Gepetto Cursos' })}
            </span>
          </div>

          <button
            type="button"
            onClick={() => setTab('perfil')}
            className="group mb-8 flex w-full cursor-pointer items-center gap-3 px-2 text-left transition-opacity hover:opacity-90"
          >
            <div className="relative flex-shrink-0">
              <div className={sidebarAvatarWrap}>
                <img src={headerAvatarSrc} alt="" className="h-full w-full object-cover" />
              </div>
              <span className="absolute -bottom-1 -right-1 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#006947] dark:border-slate-900" />
            </div>
            <div className="min-w-0 flex-1">
              <p className={`${fh} truncate font-bold text-[#2c2f30] dark:text-slate-100`}>{localUser?.name || '—'}</p>
              <p className={sidebarPlanLineClass}>{localUser?.plan?.name || t('profile:plans')}</p>
            </div>
          </button>

          <nav className="flex flex-1 flex-col space-y-1">
            <button type="button" onClick={() => setTab('perfil')} className={navItemClass(tab === 'perfil')}>
              <User size={20} className="flex-shrink-0 opacity-90" strokeWidth={tab === 'perfil' ? 2.5 : 2} />
              {t('profile:profile')}
            </button>
            <button type="button" onClick={() => setTab('colaboradores')} className={navItemClass(tab === 'colaboradores')}>
              <Users size={20} className="flex-shrink-0 opacity-90" strokeWidth={tab === 'colaboradores' ? 2.5 : 2} />
              {t('profile:collaborators')}
            </button>
            <button type="button" onClick={() => setTab('alteracoes')} className={navItemClass(tab === 'alteracoes')}>
              <History size={20} className="flex-shrink-0 opacity-90" strokeWidth={tab === 'alteracoes' ? 2.5 : 2} />
              {t('profile:lastChangesTab')}
            </button>
            {!isGuestCollabAccount && !requiresPlanSelection && (
              <div className="space-y-0.5">
                <button
                  type="button"
                  onClick={() => {
                    setTab('planos');
                    setPlansSubTab('catalog');
                  }}
                  className={navItemClass(tab === 'planos' && plansSubTab === 'catalog')}
                >
                  <Layers size={20} className="flex-shrink-0 opacity-90" strokeWidth={tab === 'planos' && plansSubTab === 'catalog' ? 2.5 : 2} />
                  {t('profile:plans')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setTab('planos');
                    setPlansSubTab('addons');
                  }}
                  className={`${navItemClass(tab === 'planos' && plansSubTab === 'addons')} pl-9 text-xs`}
                >
                  <PackagePlus size={18} className="flex-shrink-0 opacity-90" strokeWidth={tab === 'planos' && plansSubTab === 'addons' ? 2.5 : 2} />
                  {t('profile:plansSubAddons')}
                </button>
              </div>
            )}
            {!isGuestCollabAccount && requiresPlanSelection && (
              <button type="button" onClick={() => { setTab('planos'); setPlansSubTab('catalog'); }} className={navItemClass(tab === 'planos')}>
                <Layers size={20} className="flex-shrink-0 opacity-90" strokeWidth={tab === 'planos' ? 2.5 : 2} />
                {t('profile:plans')}
              </button>
            )}
            {showPaymentGatewayNavItem && (
              <button
                type="button"
                title={paymentGatewayNavLocked ? t('profile:paymentGatewayLockedHint') : undefined}
                onClick={() => {
                  if (paymentGatewayNavLocked) {
                    alert(t('profile:paymentGatewayLockedHint'));
                    return;
                  }
                  setTab('gateways');
                }}
                className={`${navItemClass(tab === 'gateways' && !paymentGatewayNavLocked)} ${
                  paymentGatewayNavLocked ? 'cursor-pointer opacity-45' : ''
                }`}
              >
                <Wallet size={20} className="flex-shrink-0 opacity-90" strokeWidth={tab === 'gateways' && !paymentGatewayNavLocked ? 2.5 : 2} />
                {t('profile:paymentGatewayNav')}
              </button>
            )}
            {showIndique && (
              <button type="button" onClick={() => setTab('indique')} className={navItemClass(tab === 'indique')}>
                <Gift size={20} className="flex-shrink-0 opacity-90" strokeWidth={tab === 'indique' ? 2.5 : 2} />
                {t('profile:referral')}
              </button>
            )}
          </nav>

          <div className="mt-auto space-y-1 border-t border-slate-200 pt-4 dark:border-slate-700">
            {showSidebarUpgrade ? (
              <button
                type="button"
                onClick={() => {
                  setTab('planos');
                  setPlansSubTab('catalog');
                }}
                className={`${fh} mb-4 w-full rounded-xl bg-gradient-to-r from-[#0546ed] to-[#003cd3] py-2.5 text-sm font-semibold text-white shadow-sm transition-transform active:scale-[0.98] dark:from-blue-600 dark:to-blue-800`}
              >
                {t('profile:upgradePlan')}
              </button>
            ) : null}
            <button type="button" onClick={() => setTab('perfil')} className={navItemClass(false)}>
              <Settings size={20} className="flex-shrink-0 opacity-90" />
              {t('profile:settingsNav')}
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-semibold text-[#a70138] transition-colors hover:bg-[#f74b6d]/10 dark:text-red-400 dark:hover:bg-red-950/40"
            >
              <LogOut size={20} className="flex-shrink-0" />
              {t('common:logout')}
            </button>
          </div>
        </aside>
      )}

      <div className={`flex min-h-screen min-w-0 w-full max-w-full flex-1 flex-col bg-[#f5f6f7] dark:bg-slate-950 ${requiresPlanSelection ? '' : 'lg:ml-64'}`}>
        <header className="sticky top-0 z-40 flex w-full items-center justify-between border-b border-slate-100 bg-[#ffffff]/80 px-4 py-4 shadow-sm backdrop-blur-md dark:border-slate-800 dark:bg-slate-900/85 md:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex flex-shrink-0 items-center gap-2 rounded-lg text-slate-600 transition-colors hover:text-[#0546ed] dark:text-slate-400 dark:hover:text-blue-400"
              >
                <ArrowLeft size={20} />
                <span className={`${fh} hidden text-sm font-semibold sm:inline`}>{t('common:back')}</span>
              </button>
            )}
            <h1 className={`${fh} truncate text-lg font-bold text-slate-900 dark:text-slate-100 md:text-xl`}>{headerTitle}</h1>
          </div>
          <div className="flex items-center gap-3 md:gap-6">
            <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 md:gap-4">
              <LanguageSwitcher />
              {onToggleDarkMode && (
                <button
                  type="button"
                  onClick={onToggleDarkMode}
                  className="rounded-lg p-1.5 text-slate-600 transition-colors hover:text-[#0546ed] dark:text-slate-400 dark:hover:text-blue-400"
                  title={darkMode ? t('common:light') : t('common:dark')}
                >
                  {darkMode ? <Sun size={22} /> : <Moon size={22} />}
                </button>
              )}
              <span className="relative rounded-lg p-1.5 text-slate-500 opacity-80 dark:text-slate-500" aria-hidden>
                <Bell size={22} />
                <span className="absolute right-0 top-0 h-2 w-2 rounded-full border border-white bg-[#b41340] dark:border-slate-900" />
              </span>
              {!requiresPlanSelection && tab === 'alteracoes' ? (
                <img
                  src={headerAvatarSrc}
                  alt=""
                  className="ml-1 hidden h-8 w-8 rounded-full border border-slate-200 object-cover dark:border-slate-600 sm:block"
                />
              ) : null}
            </div>
          </div>
        </header>

        {!requiresPlanSelection && (
          <div className="flex w-full max-w-full min-w-0 gap-1 overflow-x-auto border-b border-slate-200/80 bg-white/90 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/80 lg:hidden">
            {[
              { id: 'perfil', label: t('profile:profile') },
              { id: 'colaboradores', label: t('profile:collaborators') },
              { id: 'alteracoes', label: t('profile:lastChangesTab') },
              ...(!isGuestCollabAccount
                ? [
                    { id: 'planos_catalog', label: t('profile:plans') },
                    { id: 'planos_addons', label: t('profile:plansSubAddonsShort') },
                  ]
                : []),
              ...(showPaymentGatewayNavItem ? [{ id: 'gateways', label: t('profile:paymentGatewayNav') }] : []),
              ...(showIndique ? [{ id: 'indique', label: t('profile:referral') }] : []),
            ].map(({ id, label }) => {
              const active =
                id === 'planos_catalog'
                  ? tab === 'planos' && plansSubTab === 'catalog'
                  : id === 'planos_addons'
                    ? tab === 'planos' && plansSubTab === 'addons'
                    : id === 'gateways'
                      ? tab === 'gateways' && !paymentGatewayNavLocked
                      : tab === id;
              return (
                <button
                  key={id}
                  type="button"
                  title={id === 'gateways' && paymentGatewayNavLocked ? t('profile:paymentGatewayLockedHint') : undefined}
                  onClick={() => {
                    if (id === 'planos_catalog') {
                      setTab('planos');
                      setPlansSubTab('catalog');
                    } else if (id === 'planos_addons') {
                      setTab('planos');
                      setPlansSubTab('addons');
                    } else if (id === 'gateways') {
                      if (paymentGatewayNavLocked) {
                        alert(t('profile:paymentGatewayLockedHint'));
                        return;
                      }
                      setTab('gateways');
                    } else {
                      setTab(id);
                    }
                  }}
                  className={`whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${
                    active ? 'bg-[#0546ed] text-white shadow-sm' : 'bg-[#eff1f2] text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                  } ${id === 'gateways' && paymentGatewayNavLocked ? 'opacity-50' : ''}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        <div className={`${ATM_PAGE_MAX_CLASS} min-w-0 max-w-full flex-1 px-4 pb-12 pt-4 md:px-8 md:pb-16 md:pt-8`}>
          {requiresPlanSelection && (
            <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/40">
              <h3 className={`${fh} mb-1 font-semibold text-blue-800 dark:text-blue-200`}>{t('profile:choosePlan')}</h3>
              <p className="text-sm text-blue-700 dark:text-blue-300">{t('profile:choosePlanHint')}</p>
            </div>
          )}

          {tab === 'perfil' && !requiresPlanSelection && (
            <>
              <div className="mb-10 flex flex-col justify-between gap-6 md:flex-row md:items-end">
                <div>
                  <h2 className={`${fh} mb-2 text-3xl font-extrabold tracking-tight text-[#2c2f30] dark:text-slate-100`}>{t('profile:editProfile')}</h2>
                  <p className="font-medium text-[#595c5d] dark:text-slate-400">{t('profile:profilePageSubtitle')}</p>
                </div>
                {localUser?.plan && (
                  <div className="flex items-center gap-3 self-start rounded-full border border-[#006947]/15 bg-[#69f6b8] px-4 py-2 dark:border-emerald-500/30 dark:bg-emerald-900/40">
                    <BadgeCheck size={18} className="flex-shrink-0 text-[#005a3c] dark:text-emerald-300" strokeWidth={2.5} />
                    <span className="text-xs font-bold uppercase tracking-wider text-[#005a3c] dark:text-emerald-200">
                      {/premium/i.test(String(localUser.plan.name || ''))
                        ? t('profile:premiumActiveShort')
                        : t('profile:planActiveBadge', { plan: localUser.plan.name })}
                    </span>
                  </div>
                )}
              </div>
              <ProfileEditForm user={localUser} authToken={authToken} onSuccess={handleUserUpdate} t={t} />
              <LegalFooter className="mt-16" />
            </>
          )}

          {tab === 'colaboradores' && !requiresPlanSelection && (
            <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
              <CollaboratorsPanel authToken={authToken} user={localUser} onOpenProject={onOpenProjectById} onBack={onBack} />
            </div>
          )}

          {tab === 'alteracoes' && !requiresPlanSelection && (
            <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
              <RecentActivityPanel authToken={authToken} />
            </div>
          )}

          {tab === 'planos' && !isGuestCollabAccount && (
            <div>
              {!requiresPlanSelection && plansSubTab === 'addons' ? (
                <PlanAddonsShop
                  authToken={authToken}
                  user={localUser}
                  t={t}
                  onUserRefresh={async () => {
                    if (!authToken) return;
                    try {
                      const r = await fetch(`${API_BASE}/auth/me`, { headers: { Authorization: `Bearer ${authToken}` } });
                      const d = await r.json();
                      if (d.user) handleUserUpdate(d.user);
                    } catch {
                      /* ignore */
                    }
                  }}
                />
              ) : loading ? (
                <div className="py-16 text-center text-[#595c5d] dark:text-slate-400">{t('profile:loadingPlans')}</div>
              ) : (
                <>
                  {currentPlanId && !plans.some((p) => p.id === currentPlanId) && (
                    <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                      {t('profile:customPlanCatalogBanner')}
                    </div>
                  )}
                  <PlansTable
                    t={t}
                    plans={plans}
                    currentPlanId={currentPlanId}
                    onSelectPlan={handleSelectPlan}
                    user={localUser}
                    requiresPlanSelection={!!requiresPlanSelection}
                  />
                </>
              )}
            </div>
          )}

          {tab === 'indique' && !requiresPlanSelection && showIndique && (
            <div className="rounded-xl border border-slate-200/80 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
              <IndiquePanel user={localUser} />
            </div>
          )}

          {tab === 'gateways' && !requiresPlanSelection && showPaymentGatewayNavItem && (
            paymentGatewayNavLocked ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 shadow-sm dark:border-amber-800 dark:bg-amber-950/40">
                <h2 className={`${fh} mb-2 text-xl font-bold text-amber-950 dark:text-amber-100`}>
                  {t('profile:paymentGatewayTitle')}
                </h2>
                <p className="mb-6 text-sm leading-relaxed text-amber-900 dark:text-amber-200">
                  {t('profile:paymentGatewayLockedHint')}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setTab('planos');
                    setPlansSubTab('catalog');
                  }}
                  className={`${fh} rounded-xl bg-[#0546ed] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 dark:bg-blue-600`}
                >
                  {t('profile:upgradePlan')}
                </button>
              </div>
            ) : (
              <PaymentGatewayPanel authToken={authToken} initialProviderTab={initialPaymentGatewayProvider} />
            )
          )}
        </div>
      </div>
    </div>
  );
}
